/**
 * Local-time conversions for `<input type="datetime-local">` / `<input type="time">`.
 *
 * The daemon stores epoch seconds (UTC by definition); the forms show the
 * device's local wall-clock time. These helpers are pure and deliberately
 * strict: a non-empty but malformed value is reported as invalid instead of
 * silently becoming "no date", so a typo cannot drop a reminder.
 */

export interface ParsedDatetimeLocal {
  ok: boolean;
  /** Epoch seconds, or null for an empty input. */
  seconds: number | null;
}

export interface ParsedTimeInput {
  ok: boolean;
  hour: number;
  minute: number;
}

const DATETIME_LOCAL_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/u;
const TIME_INPUT_PATTERN = /^(\d{2}):(\d{2})$/u;

function pad(value: number, length = 2): string {
  return String(value).padStart(length, '0');
}

function validHourMinute(hour: number, minute: number): boolean {
  return (
    Number.isInteger(hour) &&
    Number.isInteger(minute) &&
    hour >= 0 &&
    hour <= 23 &&
    minute >= 0 &&
    minute <= 59
  );
}

/** Parse a `datetime-local` value ("YYYY-MM-DDTHH:mm") into epoch seconds. */
export function parseDatetimeLocal(value: string): ParsedDatetimeLocal {
  const trimmed = value.trim();
  if (trimmed === '') return { ok: true, seconds: null };
  const match = DATETIME_LOCAL_PATTERN.exec(trimmed);
  if (!match) return { ok: false, seconds: null };
  const [, rawYear, rawMonth, rawDay, rawHour, rawMinute] = match;
  if (
    rawYear === undefined ||
    rawMonth === undefined ||
    rawDay === undefined ||
    rawHour === undefined ||
    rawMinute === undefined
  ) {
    return { ok: false, seconds: null };
  }
  const year = Number(rawYear);
  const month = Number(rawMonth);
  const day = Number(rawDay);
  const hour = Number(rawHour);
  const minute = Number(rawMinute);
  if (!validHourMinute(hour, minute)) return { ok: false, seconds: null };
  const date = new Date(year, month - 1, day, hour, minute, 0, 0);
  // Reject rolled-over dates (2026-02-31 → Mar 3).
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day ||
    date.getHours() !== hour ||
    date.getMinutes() !== minute
  ) {
    return { ok: false, seconds: null };
  }
  return { ok: true, seconds: Math.floor(date.getTime() / 1000) };
}

/** Format epoch seconds as a `datetime-local` value, or '' for null. */
export function datetimeLocalFromSeconds(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) return '';
  const date = new Date(Math.floor(seconds) * 1000);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

/** Wire-format timestamp: epoch seconds → ISO 8601 UTC, or null. */
export function isoTimestampFromSeconds(seconds: number | null): string | null {
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) return null;
  return new Date(Math.floor(seconds) * 1000).toISOString();
}

/** Human-readable local date-time (`YYYY-MM-DD HH:mm`), or '—'. */
export function formatDateTime(seconds: number | null): string {
  const value = datetimeLocalFromSeconds(seconds);
  return value === '' ? '—' : value.replace('T', ' ');
}

/** `HH:mm` value for `<input type="time">`. */
export function timeInputValue(hour: number, minute: number): string {
  if (!validHourMinute(hour, minute)) return '';
  return `${pad(hour)}:${pad(minute)}`;
}

/** Parse `<input type="time">` ("HH:mm"); empty/invalid is reported as invalid. */
export function parseTimeInput(value: string): ParsedTimeInput {
  const trimmed = value.trim();
  const match = TIME_INPUT_PATTERN.exec(trimmed);
  if (!match) return { ok: false, hour: 0, minute: 0 };
  const [, rawHour, rawMinute] = match;
  const hour = Number(rawHour);
  const minute = Number(rawMinute);
  if (rawHour === undefined || rawMinute === undefined || !validHourMinute(hour, minute)) {
    return { ok: false, hour: 0, minute: 0 };
  }
  return { ok: true, hour, minute };
}

export function isValidHourMinute(hour: number, minute: number): boolean {
  return validHourMinute(hour, minute);
}
